import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { usePlatformEvents, type ConnectionDiagnostics } from '../contexts/PlatformEventContext';
import type { PlatformEvent, EventFilter } from '../types/events';
import { getRunGroupKey } from '../pages/events/useEventGrouping';
import { TERMINAL_EVENTS } from '../utils/run-events-constants';

/**
 * Cap on grouped items retained in the buffer (one run = one item; one
 * non-run event = one item). Active runs are protected from eviction —
 * only terminated runs and excess non-run events get trimmed.
 */
const MAX_GROUPED_ITEMS = 100;
/** Floor on non-run events kept around so they don't get starved out by runs. */
const NON_RUN_FLOOR = Math.floor(MAX_GROUPED_ITEMS / 4);
/** A "running" bucket idle this long is treated as terminal so it can be evicted. */
const STALE_RUN_MS = 30 * 60 * 1000;
/** How often to scan for stale running buckets. */
const STALE_SWEEP_MS = 60 * 1000;
const DEBOUNCE_MS = 300;

interface RunBucket {
  groupKey: string;
  events: PlatformEvent[]; // newest-first
  status: 'running' | 'terminal';
  terminatedAt: number | null;
  lastEventAt: number;
  seenIds: Set<string>;
}

export interface UseEventBufferReturn {
  events: PlatformEvent[];
  totalEvents: number;
  groupCount: number;
  maxGroupedItems: number;
  clearEvents: () => void;
  sourceFilter: string;
  setSourceFilter: (v: string) => void;
  eventTypeFilter: string;
  setEventTypeFilter: (v: string) => void;
  isConnected: boolean;
  diagnostics: ConnectionDiagnostics;
}

export function useEventBuffer(): UseEventBufferReturn {
  const { subscribe, isConnected, diagnostics } = usePlatformEvents();

  const [sourceFilter, setSourceFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [tick, setTick] = useState(0);

  const runBucketsRef = useRef<Map<string, RunBucket>>(new Map());
  const nonRunEventsRef = useRef<PlatformEvent[]>([]);
  const nonRunSeenRef = useRef<Set<string>>(new Set());
  const unsubRef = useRef<(() => void) | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef(false);

  const [debouncedEventType, setDebouncedEventType] = useState(eventTypeFilter);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEventType(eventTypeFilter), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [eventTypeFilter]);

  const scheduleRender = useCallback(() => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    rafRef.current = requestAnimationFrame(() => {
      pendingRef.current = false;
      setTick((t) => t + 1);
    });
  }, []);

  const enforceItemCap = useCallback(() => {
    let total = runBucketsRef.current.size + nonRunEventsRef.current.length;
    if (total <= MAX_GROUPED_ITEMS) return;

    while (total > MAX_GROUPED_ITEMS && nonRunEventsRef.current.length > NON_RUN_FLOOR) {
      const dropped = nonRunEventsRef.current.pop();
      if (dropped) nonRunSeenRef.current.delete(dropped.eventId);
      total--;
    }

    if (total > MAX_GROUPED_ITEMS) {
      const terminated = [...runBucketsRef.current.values()]
        .filter((b) => b.status === 'terminal')
        .sort((a, b) => (a.terminatedAt ?? 0) - (b.terminatedAt ?? 0));
      while (total > MAX_GROUPED_ITEMS && terminated.length > 0) {
        const victim = terminated.shift()!;
        runBucketsRef.current.delete(victim.groupKey);
        total--;
      }
    }
    // If still over cap, every remaining bucket is 'running' — leave them alone.
  }, []);

  const ingest = useCallback((event: PlatformEvent) => {
    if (!event.eventId || !event.eventType || !event.metadata?.timestamp) return;

    const groupKey = getRunGroupKey(event);
    if (groupKey) {
      let bucket = runBucketsRef.current.get(groupKey);
      if (!bucket) {
        bucket = {
          groupKey,
          events: [],
          status: 'running',
          terminatedAt: null,
          lastEventAt: Date.now(),
          seenIds: new Set(),
        };
        runBucketsRef.current.set(groupKey, bucket);
      }
      if (bucket.seenIds.has(event.eventId)) return;
      bucket.seenIds.add(event.eventId);
      bucket.events.unshift(event);
      bucket.lastEventAt = Date.now();
      const isCoordinatorTerminal =
        TERMINAL_EVENTS.has(event.eventType) && !(event.data?.parentRunId as string | undefined);
      if (isCoordinatorTerminal) {
        bucket.status = 'terminal';
        bucket.terminatedAt = Date.now();
      }
    } else {
      if (nonRunSeenRef.current.has(event.eventId)) return;
      nonRunSeenRef.current.add(event.eventId);
      nonRunEventsRef.current.unshift(event);
    }

    enforceItemCap();
    scheduleRender();
  }, [enforceItemCap, scheduleRender]);

  const doSubscribe = useCallback(() => {
    const filters: EventFilter[] = [{}];
    if (debouncedEventType) {
      filters[0].eventType = debouncedEventType;
    }
    unsubRef.current = subscribe(filters, ingest);
  }, [subscribe, debouncedEventType, ingest]);

  const doUnsubscribe = useCallback(() => {
    unsubRef.current?.();
    unsubRef.current = null;
  }, []);

  useEffect(() => {
    doSubscribe();
    return doUnsubscribe;
  }, [doSubscribe, doUnsubscribe]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      let mutated = false;
      for (const bucket of runBucketsRef.current.values()) {
        if (bucket.status === 'running' && now - bucket.lastEventAt > STALE_RUN_MS) {
          bucket.status = 'terminal';
          bucket.terminatedAt = bucket.lastEventAt;
          mutated = true;
        }
      }
      if (mutated) {
        enforceItemCap();
        scheduleRender();
      }
    }, STALE_SWEEP_MS);
    return () => clearInterval(interval);
  }, [enforceItemCap, scheduleRender]);

  const clearEvents = useCallback(() => {
    runBucketsRef.current.clear();
    nonRunEventsRef.current = [];
    nonRunSeenRef.current.clear();
    scheduleRender();
  }, [scheduleRender]);

  const events = useMemo(() => {
    const all: PlatformEvent[] = [];
    for (const bucket of runBucketsRef.current.values()) {
      for (const e of bucket.events) all.push(e);
    }
    for (const e of nonRunEventsRef.current) all.push(e);
    const filtered = sourceFilter ? all.filter((e) => e.source === sourceFilter) : all;
    return filtered.sort((a, b) => {
      const ta = a.metadata?.timestamp ?? '';
      const tb = b.metadata?.timestamp ?? '';
      return tb < ta ? -1 : tb > ta ? 1 : 0;
    });
  }, [sourceFilter, tick]);

  const totalEvents = events.length;
  const groupCount = runBucketsRef.current.size + nonRunEventsRef.current.length;

  return {
    events,
    totalEvents,
    groupCount,
    maxGroupedItems: MAX_GROUPED_ITEMS,
    clearEvents,
    sourceFilter,
    setSourceFilter,
    eventTypeFilter,
    setEventTypeFilter,
    isConnected,
    diagnostics,
  };
}
